import {
  FETCH_CONCURRENCY,
  GENERATOR_POLL_INTERVAL_MS,
  PROCESS_URLS_MAX_DURATION_MS,
  PROCESS_URLS_MAX_ITERATIONS,
  QUEUE_SIZE_MULTIPLIER,
} from "@client-shared/lib/constants";

/**
 * Streaming utilities for concurrent URL processing
 */

export type UrlItem = string | { url: string; orderIndex: number };

/**
 * Fills the URL queue from an async generator, up to the specified concurrency and maximum items.
 * Supports both string URLs and { url, orderIndex } objects.
 *
 * @template T - The type of URL item, either a string or an object with url and orderIndex.
 * @param urlGenerator - The async generator yielding URL items.
 * @param urlQueue - The queue to fill with URL items.
 * @param concurrency - The concurrency level for processing.
 * @param maxItems - The optional maximum number of items to process.
 * @param itemIndex - The current index to start filling from.
 * @returns Promise resolving to an object indicating if the generator is done and the new item index.
 */
export const fillUrlQueue = async <T extends UrlItem>(
  urlGenerator: AsyncGenerator<T, void, unknown>,
  urlQueue: T[],
  concurrency: number,
  maxItems: number | undefined,
  itemIndex: number
): Promise<{ isDone: boolean; newItemIndex: number }> => {
  let currentIndex = itemIndex;
  while (urlQueue.length < concurrency * QUEUE_SIZE_MULTIPLIER && (maxItems === undefined || currentIndex < maxItems)) {
    const result = await urlGenerator.next();
    if (result.done) {
      return { isDone: true, newItemIndex: currentIndex };
    }
    urlQueue.push(result.value);
    currentIndex++;
  }
  return { isDone: false, newItemIndex: currentIndex };
};

/**
 * Starts new fetches up to the concurrency limit.
 * Supports both string URLs and { url, orderIndex } objects.
 *
 * @param urlQueue - The queue of URLs or URL objects to process.
 * @param activeFetches - The set of currently active fetch promises.
 * @param concurrency - The maximum number of concurrent fetches allowed.
 * @param processor - The function to process each item in the queue.
 * @param startIndex - The starting index for processing items.
 * @returns The next index to use for processing.
 */
export const startNewFetches = <T extends UrlItem>(
  urlQueue: T[],
  activeFetches: Set<Promise<void>>,
  concurrency: number,
  processor: (item: T, index?: number) => Promise<void>,
  startIndex: number
): number => {
  let currentIndex = startIndex;
  while (activeFetches.size < concurrency && urlQueue.length > 0) {
    const item = urlQueue.shift();
    if (item == null) {
      break;
    }

    const fetchPromise = processor(item, currentIndex)
      .catch((error) => {
        // Log error but don't throw - allow processing to continue
        console.error(`Error processing item at index ${currentIndex}:`, error);
      })
      .finally(() => {
        activeFetches.delete(fetchPromise);
      });
    activeFetches.add(fetchPromise);
    currentIndex++;
  }
  return currentIndex;
};

/**
 * Checks if the processing loop should continue
 */
const shouldContinueLoop = <T extends UrlItem>(
  isGeneratorDone: boolean,
  urlQueue: T[],
  activeFetches: Set<Promise<void>>,
  loopIterations: number
): boolean => {
  return (!isGeneratorDone || urlQueue.length > 0 || activeFetches.size > 0) && loopIterations < PROCESS_URLS_MAX_ITERATIONS;
};

/**
 * Checks for timeout condition
 */
const checkTimeout = (startTime: number): void => {
  if (Date.now() - startTime > PROCESS_URLS_MAX_DURATION_MS) {
    console.error("processUrlsWithConcurrency: Timeout exceeded. This may indicate a bug or a hanging promise.");
    throw new Error("processUrlsWithConcurrency exceeded maximum duration");
  }
};

/**
 * Fills the queue from the generator if conditions are met
 */
const fillQueueIfNeeded = async <T extends UrlItem>(
  urlGenerator: AsyncGenerator<T, void, unknown>,
  urlQueue: T[],
  concurrency: number,
  maxItems: number | undefined,
  isGeneratorDone: boolean,
  maxItemsReached: boolean,
  itemIndex: number
): Promise<{ isGeneratorDone: boolean; itemIndex: number; maxItemsReached: boolean }> => {
  if (isGeneratorDone || maxItemsReached) {
    return { isGeneratorDone, itemIndex, maxItemsReached };
  }

  const result = await fillUrlQueue(urlGenerator, urlQueue, concurrency, maxItems, itemIndex);
  const newMaxItemsReached = maxItems !== undefined && result.newItemIndex >= maxItems;

  return {
    isGeneratorDone: result.isDone,
    itemIndex: result.newItemIndex,
    maxItemsReached: newMaxItemsReached,
  };
};

/**
 * Waits for fetches to complete or polls the generator
 */
const waitForFetchesOrPoll = async (
  activeFetches: Set<Promise<void>>,
  maxItemsReached: boolean,
  isGeneratorDone: boolean
): Promise<boolean> => {
  if (activeFetches.size > 0) {
    await Promise.race(activeFetches);
    return true; // Continue loop
  }

  if (!maxItemsReached && !isGeneratorDone) {
    await new Promise((resolve) => setTimeout(resolve, GENERATOR_POLL_INTERVAL_MS));
    return true; // Continue loop
  }

  return false; // Exit loop
};

/**
 * Shared utility for streaming PDF processing with concurrency control.
 * Supports both string URLs and { url, orderIndex } objects.
 *
 * Processes URLs from an async generator incrementally, respecting concurrency limits
 * and optional maximum item constraints. This prevents memory accumulation and
 * presigned URL timeouts by processing items as they become available.
 *
 * @template T - The type of URL item, either a string or an object with url and orderIndex.
 * @param urlGenerator - The async generator yielding URL items.
 * @param processor - Function to process each URL item.
 * @param options - Optional configuration options.
 * @param options.concurrency - Maximum number of concurrent operations (default: FETCH_CONCURRENCY).
 * @param options.maxItems - Optional maximum number of items to process.
 * @param options.signal - Optional AbortSignal to cancel the operation.
 * @returns Promise that resolves when all items are processed.
 * @throws Error if loop iteration limit is exceeded or timeout is reached.
 */
export const processUrlsWithConcurrency = async <T extends UrlItem>(
  urlGenerator: AsyncGenerator<T, void, unknown>,
  processor: (item: T, index?: number) => Promise<void>,
  options?: {
    concurrency?: number;
    maxItems?: number;
    signal?: AbortSignal;
  }
): Promise<void> => {
  const concurrency = options?.concurrency ?? FETCH_CONCURRENCY;
  const maxItems = options?.maxItems;
  const signal = options?.signal;
  const urlQueue: T[] = [];
  const activeFetches = new Set<Promise<void>>();
  let isGeneratorDone = false;
  let itemIndex = 0;

  // Check if aborted before starting
  if (signal?.aborted) {
    return;
  }

  // Loop continues while: generator has items, queue has items, or fetches are in progress
  // Note: maxItems only limits generator consumption, not loop termination
  // All queued items and active fetches must complete before loop exits
  const startTime = Date.now();
  let loopIterations = 0;
  let maxItemsReached = false;

  while (shouldContinueLoop(isGeneratorDone, urlQueue, activeFetches, loopIterations)) {
    // Check if aborted
    if (signal?.aborted) {
      // The processor should handle abort errors, but we can't cancel promises directly
      // They will be rejected when the signal is aborted
      return;
    }

    checkTimeout(startTime);
    loopIterations++;

    const queueResult: { isGeneratorDone: boolean; itemIndex: number; maxItemsReached: boolean } = await fillQueueIfNeeded(
      urlGenerator,
      urlQueue,
      concurrency,
      maxItems,
      isGeneratorDone,
      maxItemsReached,
      itemIndex
    );
    isGeneratorDone = queueResult.isGeneratorDone;
    itemIndex = queueResult.itemIndex;
    maxItemsReached = queueResult.maxItemsReached;

    // Start new fetches up to concurrency limit
    // Process all available URLs in queue, regardless of maxItems (maxItems only limits generator consumption)
    itemIndex = startNewFetches(urlQueue, activeFetches, concurrency, processor, itemIndex);

    const shouldContinue = await waitForFetchesOrPoll(activeFetches, maxItemsReached, isGeneratorDone);
    if (!shouldContinue) {
      break;
    }
  }

  if (loopIterations >= PROCESS_URLS_MAX_ITERATIONS) {
    console.error("processUrlsWithConcurrency: Loop iteration limit reached. This may indicate a bug.");
    throw new Error("processUrlsWithConcurrency loop exceeded maximum iterations - possible infinite loop");
  }

  // Wait for all remaining fetches to complete (unless aborted)
  if (!signal?.aborted) {
    await Promise.all(activeFetches);
  }
};
